<template>
    <div class="plan-wrapper">
        <!-- Breadcrumb -->
        <div class="breadcrumb-pm">
            <router-link to="/">Dashboard</router-link>
            <span class="crumb-sep">/</span>
            <span class="crumb-current">Plans & Pricing</span>
        </div>

        <div class="pm-header">
            <div>
                <h1 class="pm-title">Plans & Pricing</h1>
                <p class="pm-subtitle">Define subscription tiers, usage limits, and module access for your companies.</p>
            </div>
            <button class="btn-add-plan" @click="openCreateModal">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                New Plan
            </button>
        </div>

        <!-- Plan cards -->
        <div v-if="plans.length" class="plans-grid">
            <div v-for="plan in plans" :key="plan.id" class="plan-card" :class="{ inactive: !plan.is_active }">
                <div v-if="!plan.is_active" class="plan-inactive-tag">Inactive</div>
                <div class="plan-card-head">
                    <h3 class="plan-name">{{ plan.name }}</h3>
                    <div class="plan-price">
                        <span class="price-amount">₹{{ formatPrice(plan.price) }}</span>
                        <span class="price-period">/ {{ formatDuration(plan.duration_days) }}</span>
                    </div>
                </div>

                <ul class="plan-limits">
                    <li>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        {{ plan.max_pdfs_per_month ?? 'Unlimited' }} PDFs/month
                    </li>
                    <li>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        {{ plan.max_licenses ?? 'Unlimited' }} licenses (users)
                    </li>
                </ul>

                <div class="plan-modules">
                    <span v-for="m in plan.enabled_modules" :key="m" class="module-chip">{{ moduleLabel(m) }}</span>
                    <span v-if="!plan.enabled_modules || !plan.enabled_modules.length" class="module-chip none">No modules enabled</span>
                </div>

                <div class="plan-card-actions">
                    <button class="btn-edit" @click="openEditModal(plan)">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Edit
                    </button>
                    <button class="btn-delete" @click="confirmDelete(plan)">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <div v-else class="empty-plans">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            <p>No plans created yet.</p>
            <button @click="openCreateModal" class="empty-cta">Create your first plan</button>
        </div>

        <!-- Create/Edit Modal -->
        <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
            <div class="modal-content">
                <div class="modal-header">
                    <div class="modal-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                    </div>
                    <div>
                        <h2>{{ isEditing ? 'Edit Plan' : 'Create New Plan' }}</h2>
                        <p>Define pricing, limits, and module access</p>
                    </div>
                </div>

                <div class="modal-body">
                    <form @submit.prevent="savePlan" class="modal-form">
                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Plan Name</label>
                                <input type="text" v-model="form.name" required placeholder="e.g., Pro" class="form-input-premium">
                            </div>
                            <div class="form-group">
                                <label class="form-label">Price (₹)</label>
                                <input type="number" v-model.number="form.price" min="0" required placeholder="0" class="form-input-premium">
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Billing Duration</label>
                            <select v-model.number="form.duration_days" required class="form-input-premium">
                                <option :value="30">Monthly (30 days)</option>
                                <option :value="90">Quarterly (90 days)</option>
                                <option :value="365">Yearly (365 days)</option>
                            </select>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label class="form-label">Max PDFs/Month</label>
                                <input type="number" v-model.number="form.max_pdfs_per_month" min="0" placeholder="Unlimited" class="form-input-premium">
                            </div>
                            <div class="form-group">
                                <label class="form-label">Max Licenses</label>
                                <input type="number" v-model.number="form.max_licenses" min="0" placeholder="Unlimited" class="form-input-premium">
                            </div>
                        </div>
                        <p class="field-hint">Max Licenses also caps total users — one license is one user seat. Leave a limit blank for unlimited.</p>

                        <div class="form-group">
                            <label class="form-label">Enabled Modules</label>
                            <div class="module-checkboxes">
                                <label v-for="m in availableModules" :key="m.value" class="module-checkbox">
                                    <input type="checkbox" :value="m.value" v-model="form.enabled_modules">
                                    {{ m.label }}
                                </label>
                            </div>
                        </div>

                        <label class="active-toggle">
                            <input type="checkbox" v-model="form.is_active">
                            Plan is active (visible for assignment)
                        </label>

                        <div v-if="validationError" class="validation-error">{{ validationError }}</div>

                        <div class="modal-actions">
                            <button type="button" @click="showModal = false" class="btn-cancel">Cancel</button>
                            <button type="submit" :disabled="loading" class="btn-submit">
                                <span v-if="loading" class="spinner-small"></span>
                                {{ loading ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Plan') }}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Delete confirm -->
        <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
            <div class="modal-content confirm-content">
                <div class="confirm-icon">
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2">
                        <path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                </div>
                <h3>Delete Plan?</h3>
                <p>Are you sure you want to delete "{{ planToDelete?.name }}"? If it's assigned to any company it will be deactivated instead.</p>
                <div class="modal-actions">
                    <button class="btn-cancel" @click="showDeleteModal = false">Cancel</button>
                    <button class="btn-submit danger" :disabled="loading" @click="deletePlan">
                        <span v-if="loading" class="spinner-small"></span>
                        {{ loading ? 'Deleting...' : 'Yes, Delete' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Toast -->
        <div v-if="successMessage" class="toast-notification">
            <div class="toast-body">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <span>{{ successMessage }}</span>
            </div>
        </div>

        <!-- Error Toast -->
        <div v-if="errorMessage" class="toast-notification">
            <div class="toast-body error">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{{ errorMessage }}</span>
                <button class="toast-close-btn" @click="errorMessage = ''">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

const MODULES = [
    { value: 'tender_search', label: 'Tender Search & Bookmarks' },
    { value: 'bid_management', label: 'Bid Management (Bids, Projects, Knowledge Base)' },
];

function emptyForm() {
    return {
        id: null,
        name: '',
        price: 0,
        duration_days: 30,
        max_pdfs_per_month: null,
        max_licenses: null,
        enabled_modules: [],
        is_active: true,
    };
}

export default {
    name: 'PlanManagement',
    data() {
        return {
            plans: [],
            availableModules: MODULES,
            showModal: false,
            showDeleteModal: false,
            isEditing: false,
            loading: false,
            validationError: '',
            successMessage: '',
            errorMessage: '',
            form: emptyForm(),
            planToDelete: null,
        }
    },
    async mounted() {
        await this.fetchPlans();
    },
    methods: {
        showError(message) {
            this.errorMessage = message;
            setTimeout(() => { this.errorMessage = ''; }, 5000);
        },
        authHeaders() {
            return { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` };
        },
        async fetchPlans() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/plans?page_size=100`, {
                    headers: this.authHeaders()
                });
                this.plans = response.data.items || [];
            } catch (error) {
                console.error('Error fetching plans:', error);
            }
        },
        moduleLabel(value) {
            const m = MODULES.find(x => x.value === value);
            return m ? m.label : value;
        },
        formatPrice(price) {
            return (price || 0).toLocaleString('en-IN');
        },
        formatDuration(days) {
            if (days === 30) return 'month';
            if (days === 90) return 'quarter';
            if (days === 365) return 'year';
            return `${days} days`;
        },
        openCreateModal() {
            this.isEditing = false;
            this.form = emptyForm();
            this.validationError = '';
            this.showModal = true;
        },
        openEditModal(plan) {
            this.isEditing = true;
            this.form = {
                id: plan.id,
                name: plan.name,
                price: plan.price,
                duration_days: plan.duration_days,
                max_pdfs_per_month: plan.max_pdfs_per_month,
                max_licenses: plan.max_licenses,
                enabled_modules: [...(plan.enabled_modules || [])],
                is_active: plan.is_active,
            };
            this.validationError = '';
            this.showModal = true;
        },
        async savePlan() {
            if (this.loading) return;
            this.validationError = '';
            if (!this.form.name.trim()) { this.validationError = 'Plan name is required'; return; }

            this.loading = true;
            try {
                const params = new URLSearchParams();
                params.append('name', this.form.name);
                params.append('price', this.form.price || 0);
                params.append('duration_days', this.form.duration_days);
                if (this.form.max_pdfs_per_month !== null && this.form.max_pdfs_per_month !== '') params.append('max_pdfs_per_month', this.form.max_pdfs_per_month);
                if (this.form.max_licenses !== null && this.form.max_licenses !== '') params.append('max_licenses', this.form.max_licenses);
                this.form.enabled_modules.forEach(m => params.append('enabled_modules', m));
                if (this.isEditing) params.append('is_active', this.form.is_active);

                if (this.isEditing) {
                    await axios.put(`${this.$API_URL}/super_admin/plan/${this.form.id}?${params.toString()}`, {}, {
                        headers: this.authHeaders()
                    });
                    this.successMessage = 'Plan updated successfully';
                } else {
                    await axios.post(`${this.$API_URL}/super_admin/plans?${params.toString()}`, {}, {
                        headers: this.authHeaders()
                    });
                    this.successMessage = 'Plan created successfully';
                }

                this.showModal = false;
                await this.fetchPlans();
                setTimeout(() => { this.successMessage = ''; }, 3000);
            } catch (error) {
                this.validationError = error.response?.data?.detail || error.response?.data?.message || 'Failed to save plan';
            } finally {
                this.loading = false;
            }
        },
        confirmDelete(plan) {
            this.planToDelete = plan;
            this.showDeleteModal = true;
        },
        async deletePlan() {
            if (this.loading || !this.planToDelete) return;
            this.loading = true;
            try {
                const response = await axios.delete(`${this.$API_URL}/super_admin/plan/${this.planToDelete.id}`, {
                    headers: this.authHeaders()
                });
                this.successMessage = response.data.message || 'Plan removed successfully';
                this.showDeleteModal = false;
                await this.fetchPlans();
                setTimeout(() => { this.successMessage = ''; }, 3000);
            } catch (error) {
                this.showError(error.response?.data?.message || error.response?.data?.detail || 'Failed to delete plan');
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style scoped>
.plan-wrapper {
    font-family: var(--font-sans);
}

.breadcrumb-pm {
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--fs-sm);
}

.breadcrumb-pm a {
    color: var(--text-secondary);
    text-decoration: none;
    font-weight: 500;
}

.breadcrumb-pm a:hover { color: #0071C5; }

.crumb-sep { color: #cbd5e1; }
.crumb-current { color: #0071C5; font-weight: 700; }

.pm-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 2rem;
}

.pm-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: var(--fs-h1);
    line-height: var(--lh-h1);
    font-weight: var(--fw-h1);
    color: var(--text-primary);
    letter-spacing: -0.02em;
}

.pm-subtitle {
    color: var(--text-secondary);
    margin: var(--space-heading-body) 0 0;
    font-size: var(--fs-sm);
}

.btn-add-plan {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background: linear-gradient(90deg, #0071C5, #0084FF);
    color: #fff;
    border: none;
    padding: 0.75rem 1.5rem;
    border-radius: 12px;
    font-weight: 700;
    font-size: var(--fs-btn);
    font-family: var(--font-sans);
    cursor: pointer;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.35);
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-add-plan:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 22px rgba(0, 113, 197, 0.45);
}

/* Plan cards */
.plans-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
}

.plan-card {
    position: relative;
    background: #fff;
    border: 1px solid #eef2f7;
    border-radius: 20px;
    padding: 1.5rem;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.04);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    transition: transform 0.2s;
}

.plan-card:hover { transform: translateY(-3px); }
.plan-card.inactive { opacity: 0.6; }

.plan-inactive-tag {
    position: absolute;
    top: 1rem;
    right: 1rem;
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
    padding: 3px 10px;
    border-radius: 20px;
}

.plan-name {
    margin: 0 0 0.4rem;
    font-size: var(--fs-h3);
    font-weight: var(--fw-h3);
    color: var(--text-primary);
}

.plan-price {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.price-amount {
    font-family: var(--font-display);
    font-size: 1.75rem;
    font-weight: 700;
    color: #0071C5;
}

.price-period {
    font-size: 0.85rem;
    color: var(--text-secondary);
    font-weight: 500;
}

.plan-limits {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f1f5f9;
    padding: 0.85rem 0;
}

.plan-limits li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    color: var(--text-primary);
    font-weight: 600;
}

.plan-limits svg { color: #0071C5; flex-shrink: 0; }

.plan-modules {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.module-chip {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 20px;
    background: rgba(0, 113, 197, 0.08);
    color: #0071C5;
}

.module-chip.none {
    background: #f1f5f9;
    color: #94a3b8;
}

.plan-card-actions {
    display: flex;
    gap: 8px;
    margin-top: auto;
}

.btn-edit {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0.6rem;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: var(--text-secondary);
    font-weight: 700;
    font-size: 0.82rem;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-edit:hover { border-color: #0071C5; color: #0071C5; background: white; }

.btn-delete {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-delete:hover { border-color: #ef4444; color: #ef4444; background: white; }

.empty-plans {
    padding: 5rem 2rem;
    text-align: center;
    color: #94a3b8;
    background: #fff;
    border-radius: 20px;
    border: 1px dashed #e2e8f0;
}

.empty-plans svg { opacity: 0.3; margin-bottom: 1rem; }
.empty-plans p { font-size: var(--fs-body-lg); font-weight: 500; margin-bottom: 1rem; }

.empty-cta {
    color: #0071C5;
    background: none;
    border: none;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
    font-size: var(--fs-body);
}

/* Modal (shared pattern with CompanyManagement) */
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(4px);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    overflow-y: auto;
}

.modal-content {
    background: white;
    width: 100%;
    max-width: 560px;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    animation: modalSlideUp 0.3s ease-out;
}

.confirm-content {
    max-width: 400px;
    text-align: center;
    padding: 2.5rem 2rem;
}

.confirm-icon {
    background: rgba(239, 68, 68, 0.08);
    width: 70px;
    height: 70px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    border: 1px solid rgba(239, 68, 68, 0.1);
}

.confirm-content h3 {
    color: var(--text-primary);
    font-size: 1.4rem;
    font-weight: 700;
    margin-bottom: 0.75rem;
}

.confirm-content p {
    color: var(--text-secondary);
    font-size: 0.92rem;
    line-height: 1.5;
    margin-bottom: 1.5rem;
}

.modal-header {
    background: #f8fafc;
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.modal-header h2 {
    margin: 0;
    font-size: var(--fs-h3);
    font-weight: 800;
    color: var(--text-primary);
}

.modal-header p {
    margin: 0;
    font-size: 0.8rem;
    color: var(--text-secondary);
    font-weight: 500;
}

.modal-icon {
    background: #0071C5;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
}

.modal-body { padding: 2rem; max-height: 70vh; overflow-y: auto; }

.modal-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
}

.form-label {
    font-size: var(--fs-label);
    font-weight: var(--fw-label);
    letter-spacing: var(--tracking-label);
    text-transform: uppercase;
    color: var(--text-label);
    margin-bottom: 0.4rem;
    display: block;
}

.form-input-premium {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.95rem;
    font-family: var(--font-sans);
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
    background: white;
}

.form-input-premium:focus {
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
}

.field-hint {
    margin: -0.75rem 0 0;
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 500;
}

.module-checkboxes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
}

.module-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-primary);
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 0.6rem 0.8rem;
    border-radius: 10px;
    cursor: pointer;
}

.module-checkbox input { accent-color: #0071C5; }

.active-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--text-primary);
}

.active-toggle input { accent-color: #0071C5; width: 16px; height: 16px; }

.validation-error {
    background: #fef2f2;
    color: #ef4444;
    padding: 0.75rem;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    text-align: center;
    border: 1px solid #fee2e2;
}

.modal-actions {
    margin-top: 0.5rem;
    display: flex;
    gap: 1rem;
}

.btn-cancel {
    flex: 1;
    padding: 0.85rem;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
    background: white;
    color: var(--text-secondary);
    font-weight: 600;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel:hover { background: #f8fafc; }

.btn-submit {
    flex: 2;
    padding: 0.85rem;
    border-radius: 12px;
    border: none;
    background: #0071C5;
    color: white;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 12px rgba(0, 113, 197, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-submit:hover:not(:disabled) { background: #005A9E; }
.btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
.btn-submit.danger { background: #ef4444; }
.btn-submit.danger:hover:not(:disabled) { background: #dc2626; }

@keyframes modalSlideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

.spinner-small {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.toast-notification {
    position: fixed;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 2000;
}

.toast-body {
    background: #10b981;
    color: white;
    padding: 12px 24px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 700;
    box-shadow: 0 10px 25px -5px rgba(16, 185, 129, 0.4);
    animation: toastSlideDown 0.3s ease-out;
}

.toast-body.error {
    background: #ef4444;
    box-shadow: 0 10px 25px -5px rgba(239, 68, 68, 0.4);
}

.toast-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px;
    margin-left: 4px;
    border-radius: 4px;
    color: white;
    opacity: 0.85;
    flex-shrink: 0;
}

.toast-close-btn:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.15);
}

@keyframes toastSlideDown {
    from { transform: translateY(-40px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
}

@media (max-width: 640px) {
    .form-row { grid-template-columns: 1fr; }
    .module-checkboxes { grid-template-columns: 1fr; }
}
</style>
